import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { TID } from "@/lib/testIds";
import TextReveal from "./TextReveal";

const CASES = [
  {
    n: "01",
    client: "Earth Industrial Park",
    location: "Panipat, Haryana",
    sector: "Industrial Real Estate Development",
    did: "Structured and filed the client's application under the PADMA Policy (Government of Haryana) for cluster infrastructure development incentives.",
    result: { headline: "₹35 Cr", sub: "sanctioned · ₹12.5 Cr disbursed · balance in progress" },
    img: "https://images.pexels.com/photos/14804699/pexels-photo-14804699.jpeg",
  },
  {
    n: "02",
    client: "Gayatri Overseas",
    location: "Panipat, Haryana",
    sector: "Textile Manufacturing",
    did: "Assisted the client in setting up a new Effluent Treatment Plant (ETP) and structured their subsidy application under the Atmanirbhar Textile Policy.",
    result: { headline: "₹70 L", sub: "subsidy secured for water treatment infrastructure" },
    img: "https://images.unsplash.com/photo-1716643863806-989dd76ae093?crop=entropy&cs=srgb&fm=jpg",
  },
  {
    n: "03",
    client: "GM Foods Pvt. Ltd.",
    location: "Sonipat, Haryana",
    sector: "Food Processing · Spices · RTC / RTE",
    did: "Structured and filed the client's grant application for their manufacturing facility.",
    result: { headline: "₹1.25 Cr", sub: "grant received · ₹2.5 Cr total sanction" },
    img: "https://images.pexels.com/photos/32140172/pexels-photo-32140172.jpeg",
  },
];

// Panels in the horizontal track: intro + 3 cases + outro = 5
const PANEL_COUNT = CASES.length + 2;
// Outer scroll length keeps the pinned track on-screen; larger → slower.
// Feels good around ~1.4 viewports per panel.
const SCROLL_MULTIPLIER = PANEL_COUNT * 1.15;

export const CaseStudies = () => {
  const outerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: outerRef,
    offset: ["start start", "end end"],
  });
  // Advance the track by (PANEL_COUNT - 1) viewport-widths worth of translation
  const x = useTransform(
    scrollYProgress,
    [0, 1],
    ["0vw", `-${(PANEL_COUNT - 1) * 100}vw`],
  );

  return (
    <section
      id="case-studies"
      data-testid={TID.cases.root}
      className="relative bg-navy text-white noise"
    >
      {/* Brand accent orbs — clipped by a bounded absolute wrapper so they
          don't force `overflow-hidden` on the section itself (which would
          break the sticky pin of the horizontal-scroll track). */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-[560px] w-[560px] rounded-full bg-[color:var(--violet)]/40 blur-[160px]" />
        <div className="absolute -bottom-40 -left-40 h-[520px] w-[520px] rounded-full bg-[color:var(--gold)]/20 blur-[150px]" />
      </div>

      {/* Mobile / small screens: stacked (no pin) */}
      <div className="lg:hidden py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <SectionHead />
          <div className="mt-14 space-y-16">
            {CASES.map((c) => (
              <CaseCard key={c.n} c={c} />
            ))}
          </div>
          <MobileCta />
        </div>
      </div>

      {/* Desktop: framer-motion horizontal-scroll pinned track */}
      <div
        ref={outerRef}
        className="hidden lg:block relative"
        style={{ height: `${SCROLL_MULTIPLIER * 100}vh` }}
      >
        <div
          className="sticky top-0 overflow-hidden"
          style={{ height: "100vh", width: "100%" }}
        >
          <motion.div
            style={{ x }}
            className="absolute inset-y-0 left-0 flex items-center will-change-transform"
          >
            {/* Intro panel */}
            <HPanel>
              <div className="grid grid-cols-12 gap-10">
                <div className="col-span-12 md:col-span-4">
                  <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-[color:var(--gold)]">
                    05 — Ledger · Real outcomes
                  </p>
                  <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.28em] text-white/50">
                    Scroll →
                  </p>
                </div>
                <div className="col-span-12 md:col-span-8">
                  <h2 className="font-serif tracking-tight text-5xl md:text-7xl leading-[1.0]">
                    The numbers we have{" "}
                    <span className="italic text-[color:var(--gold)]">
                      actually
                    </span>{" "}
                    moved for our clients.
                  </h2>
                  <p className="mt-8 text-white/70 max-w-xl">
                    Three files. One thread — money moved from a government
                    department&rsquo;s desk into a promoter&rsquo;s account.
                  </p>
                </div>
              </div>
            </HPanel>

            {CASES.map((c, i) => (
              <HorizontalPanel
                key={c.n}
                c={c}
                index={i}
                totalPanels={PANEL_COUNT}
                progress={scrollYProgress}
              />
            ))}

            {/* Outro / CTA panel */}
            <HPanel>
              <div className="grid grid-cols-12 gap-10">
                <div className="col-span-12 md:col-span-6">
                  <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-[color:var(--gold)]">
                    End of ledger
                  </p>
                  <h3 className="mt-6 font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight">
                    <TextReveal>
                      Every case is different — the numbers reflect the outcome, not a guarantee.
                    </TextReveal>
                  </h3>
                </div>
                <div className="col-span-12 md:col-span-6 md:pl-10 flex flex-col justify-end">
                  <p className="text-white/70 max-w-lg">
                    <TextReveal>
                      Talk to us about what's possible for your business — DPIIT-recognized, in-house advocates, department-facing since day one.
                    </TextReveal>
                  </p>
                  <a
                    href="#contact"
                    data-testid={TID.cases.cta}
                    className="mt-8 inline-flex items-center gap-3 bg-[color:var(--gold)] px-6 py-4 text-navy hover:bg-white transition-colors w-fit"
                  >
                    <span className="font-mono text-[11px] uppercase tracking-[0.24em]">
                      Discuss my case
                    </span>
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </HPanel>
          </motion.div>

          {/* Fixed progress rail */}
          <ProgressRail progress={scrollYProgress} />
        </div>
      </div>
    </section>
  );
};

// A generic full-viewport horizontal panel
const HPanel = ({ children }) => (
  <div className="shrink-0 w-screen h-screen flex items-center">
    <div className="mx-auto max-w-[1400px] w-full px-5 sm:px-8 lg:px-12">
      {children}
    </div>
  </div>
);

// One case-study panel with a framer-motion scale-in image parallax
const HorizontalPanel = ({ c, index, totalPanels, progress }) => {
  // Panel is at scrollY progress: (index + 1) / (totalPanels - 1)
  const p = (index + 1) / (totalPanels - 1);
  const window = 1 / (totalPanels - 1);
  const scale = useTransform(
    progress,
    [p - window, p, p + window],
    [1.18, 1, 1.06],
  );

  return (
    <div className="shrink-0 w-screen h-screen flex items-center">
      <div className="mx-auto max-w-[1400px] w-full px-5 sm:px-8 lg:px-12 grid grid-cols-12 gap-10 items-center">
        <div className="col-span-7">
          <div className="relative">
            <div
              className="absolute -inset-6 border border-[color:var(--gold)]/40 pointer-events-none"
              aria-hidden
            />
            <div className="clip-frame overflow-hidden">
              <motion.img
                src={c.img}
                alt={`${c.client} — ${c.sector}`}
                style={{ scale }}
                className="w-full h-[60vh] object-cover grayscale-[30%]"
              />
            </div>
            <div className="absolute top-6 left-6 font-mono text-[10px] uppercase tracking-[0.28em] text-white bg-navy/70 backdrop-blur px-3 py-2 border border-white/20">
              Case {c.n} · {c.location}
            </div>
          </div>
        </div>
        <div className="col-span-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-white/50">
            <TextReveal>{c.sector}</TextReveal>
          </p>
          <h3 className="mt-4 font-serif tracking-tight text-4xl md:text-5xl leading-[1.05]">
            <TextReveal>{c.client}</TextReveal>
          </h3>
          <p className="mt-6 text-white/70 text-[15px] leading-relaxed max-w-lg">
            <TextReveal>{c.did}</TextReveal>
          </p>
          <div className="mt-8 border-t border-white/10 pt-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/50">
              Result
            </p>
            <div className="mt-2 flex items-baseline gap-3 flex-wrap">
              <TextReveal
                as="span"
                className="font-serif text-7xl md:text-8xl text-[color:var(--gold)] tracking-tight"
              >
                {c.result.headline}
              </TextReveal>
              <span className="text-white/70 text-sm md:text-[15px] max-w-[26ch]">
                <TextReveal>{c.result.sub}</TextReveal>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProgressRail = ({ progress }) => {
  const width = useTransform(progress, [0, 1], ["0%", "100%"]);
  return (
    <div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-white/60">
      <span>Horizontal</span>
      <span className="relative h-px w-40 bg-white/15 overflow-hidden">
        <motion.span
          style={{ width }}
          className="absolute inset-y-0 left-0 bg-[color:var(--gold)]"
        />
      </span>
      <span>Scroll</span>
    </div>
  );
};

const SectionHead = () => (
  <div className="grid grid-cols-12 gap-6 pb-14 border-b border-white/10">
    <div className="col-span-12 md:col-span-4">
      <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-[color:var(--gold)]">
        05 — Ledger · Real outcomes
      </p>
    </div>
    <div className="col-span-12 md:col-span-8">
      <TextReveal as="h2" className="font-serif tracking-tight text-4xl md:text-6xl leading-[1.02]">
        The numbers we have actually moved for our clients.
      </TextReveal>
    </div>
  </div>
);

const CaseCard = ({ c }) => (
  <article className="grid grid-cols-12 gap-6 items-start">
    <div className="col-span-12">
      <div className="relative">
        <div className="absolute -inset-4 border border-[color:var(--gold)]/40 pointer-events-none" aria-hidden />
        <div className="clip-frame overflow-hidden">
          <img
            src={c.img}
            alt={`${c.client} — ${c.sector}`}
            className="w-full h-[300px] object-cover grayscale-[30%]"
          />
        </div>
        <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.28em] text-white bg-navy/70 backdrop-blur px-3 py-2 border border-white/20">
          Case {c.n} · {c.location}
        </div>
      </div>
    </div>
    <div className="col-span-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-white/50">
        <TextReveal>{c.sector}</TextReveal>
      </p>
      <h3 className="mt-3 font-serif tracking-tight text-3xl leading-[1.05]">
        <TextReveal>{c.client}</TextReveal>
      </h3>
      <p className="mt-4 text-white/70 text-[15px] leading-relaxed">
        <TextReveal>{c.did}</TextReveal>
      </p>
      <div className="mt-6 border-t border-white/10 pt-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/50">
          Result
        </p>
        <div className="mt-2 flex items-baseline gap-3 flex-wrap">
          <TextReveal as="span" className="font-serif text-6xl text-[color:var(--gold)] tracking-tight">
            {c.result.headline}
          </TextReveal>
          <span className="text-white/70 text-sm">
            <TextReveal>{c.result.sub}</TextReveal>
          </span>
        </div>
      </div>
    </div>
  </article>
);

const MobileCta = () => (
  <div className="mt-16 border-t border-white/10 pt-10 flex flex-col gap-6">
    <p className="text-white/70 text-sm">
      Every case is different — the numbers reflect the outcome, not a guarantee.
      Talk to us about what&rsquo;s possible for your business.
    </p>
    <a
      href="#contact"
      data-testid={TID.cases.cta}
      className="inline-flex items-center gap-3 bg-[color:var(--gold)] px-6 py-4 text-navy hover:bg-white transition-colors w-fit"
    >
      <span className="font-mono text-[11px] uppercase tracking-[0.24em]">
        Discuss my case
      </span>
      <ArrowUpRight className="h-4 w-4" />
    </a>
  </div>
);

export default CaseStudies;
